import { useEffect, useRef, useState } from "react";
import { useTranslation, type TranslatedString } from "@i18n";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import IconComponent from "@CubeComponents/icons";
import type { CameraCaptureHandle, CameraCaptureState } from "./useCameraCapture";

const CameraCaptureModals = ({ capture }: { capture: CameraCaptureHandle }) => {
  const { t } = useTranslation();
  const {
    captureState,
    capturedImageUrl,
    zoomPercent,
    rotation,
    zoom,
    isPreviewMirrored,
    attachVideoElement,
    selectBrowse,
    selectCamera,
    retryCamera,
    capturePhoto,
    retakePhoto,
    zoomIn,
    zoomOut,
    rotateLeft,
    rotateRight,
    resetAdjustments,
    confirmImage,
    closeCapture,
  } = capture;

  const reviewContainerRef = useRef<HTMLDivElement | null>(null);
  const reviewImageRef = useRef<HTMLImageElement | null>(null);
  // `rotate()` doesn't affect layout, so at 90/270 the image's visual box outgrows the
  // overflow-hidden container and zoom can't go below 100% to reveal it — shrink the transform by
  // the factor that fits the swapped axes instead.
  const [rotatedFitScale, setRotatedFitScale] = useState(1);

  useEffect(() => {
    if (rotation % 180 === 0) {
      setRotatedFitScale(1);
      return;
    }
    const container = reviewContainerRef.current;
    const image = reviewImageRef.current;
    if (!container?.clientWidth || !image?.offsetWidth || !image.offsetHeight) return;
    setRotatedFitScale(
      Math.min(container.clientWidth / image.offsetHeight, container.clientHeight / image.offsetWidth, 1),
    );
  }, [rotation]);

  /**
   * The two dead-end modals — access refused and no camera to open — differ only in illustration
   * and copy: same width, same retry/cancel pair. One renderer keeps them from drifting apart.
   */
  const cameraFailureModal = ({
    state,
    illustration,
    title,
    message,
  }: {
    state: CameraCaptureState;
    illustration: string;
    title: TranslatedString;
    message: TranslatedString;
  }) => (
    <ModalStateDialogComponent
      width="40rem"
      onCancel={closeCapture}
      isVisible={captureState === state}
      modelContent={
        <div className="flex w-full flex-col items-center justify-center gap-3 p-6 text-center">
          <IconComponent name={illustration} width="14rem" height="14rem" />
          <span className="text-2xl font-semibold">{title}</span>
          <span>{message}</span>
          <div className="flex gap-4 pt-2">
            <ButtonComponent onClick={retryCamera} buttonType={BUTTON_TYPES.BORDER_BUTTON}>
              {t("Retry")}
            </ButtonComponent>
            <ButtonComponent onClick={closeCapture} buttonType={BUTTON_TYPES.BORDER_BUTTON}>
              {t("Cancel")}
            </ButtonComponent>
          </div>
        </div>
      }
    />
  );

  const sourceOption = (icon: React.ReactNode, label: string, onClick: () => void) => (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
      className="flex flex-1 cursor-pointer flex-col items-center justify-center gap-2 rounded border-2 border-solid border-button_light p-6 hover:bg-button_light"
    >
      {icon}
      <span>{label}</span>
    </div>
  );

  return (
    <>
      <ModalStateDialogComponent
        headerTitle={<div className="flex justify-between w-full">{t("Select option")}</div>}
        width="26rem"
        onCancel={closeCapture}
        isVisible={captureState === "selectOption"}
        modelContent={
          <div className="flex w-full gap-4 pb-2">
            {sourceOption(<IconComponent name="Camera" />, t("Camera"), selectCamera)}
            {sourceOption(<IconComponent name="FileUpload" />, t("Browse"), selectBrowse)}
          </div>
        }
      />

      {cameraFailureModal({
        state: "permissionDenied",
        illustration: "NoSub",
        title: t("Camera access is required for this field"),
        message: t(
          "Camera access is required for this field and isn't currently available. Please enable camera permissions or switch devices to continue.",
        ),
      })}

      {cameraFailureModal({
        state: "cameraUnavailable",
        illustration: "cameraNotFound",
        title: t("Camera is not available on this device"),
        message: t(
          "We couldn't find a camera on this device, or it is already in use by another application. Connect a camera or switch devices to continue.",
        ),
      })}

      <ModalStateDialogComponent
        headerTitle={<div className="flex p-3 justify-between w-full">{t("Camera Live")}</div>}
        width="90%"
        height="95%"
        modalPadding={0}
        onCancel={closeCapture}
        isVisible={captureState === "camera"}
        modelContent={
          <div className="flex h-full w-full flex-col">
            {/* Selfie-facing cameras preview as a mirror; `capturePhoto` bakes the same flip into
                the canvas so the photo matches what was framed here. */}
            <video
              ref={attachVideoElement}
              autoPlay
              playsInline
              muted
              className="min-h-0 w-full flex-1 bg-black object-contain"
              style={isPreviewMirrored ? { transform: "scaleX(-1)" } : undefined}
            />
            <div className="flex w-full items-center justify-center py-3">
              <span
                role="button"
                tabIndex={0}
                aria-label={t("Capture photo")}
                onClick={capturePhoto}
                onKeyDown={(e) => e.key === "Enter" && capturePhoto()}
                data-testid="cameraShutterButton"
                className="flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border-2 border-solid"
                style={{ borderColor: "var(--primary-text)" }}
              >
                <span className="h-8 w-8 rounded-full" style={{ background: "var(--primary-text)" }} />
              </span>
            </div>
          </div>
        }
      />

      <ModalStateDialogComponent
        headerTitle={<div className="flex justify-between w-full">{t("Review Image")}</div>}
        width="90%"
        height="95%"
        onCancel={closeCapture}
        isVisible={captureState === "review"}
        modelContent={
          <div className="flex h-full w-full flex-col gap-1">
            <div className="flex items-center gap-1">
              <ButtonComponent onClick={retakePhoto} buttonType={BUTTON_TYPES.TEXT_BUTTON}>
                <span className="flex items-center gap-1">
                  <IconComponent name="Camera" width="1rem" height="1rem" />
                  <span>{t("Retake")}</span>
                </span>
              </ButtonComponent>
              <ButtonComponent onClick={resetAdjustments} buttonType={BUTTON_TYPES.TEXT_BUTTON}>
                <span className="flex items-center gap-1">
                  <IconComponent name="refresh" width="1rem" height="1rem" />
                  <span>{t("Reset")}</span>
                </span>
              </ButtonComponent>
            </div>
            <div className="relative min-h-0 w-full flex-1 overflow-hidden rounded border-2 border-solid border-button_light">
              <div ref={reviewContainerRef} className="flex h-full w-full items-center justify-center overflow-hidden">
                {capturedImageUrl && (
                  <img
                    ref={reviewImageRef}
                    src={capturedImageUrl}
                    alt={t("Review Image")}
                    className="max-h-full max-w-full"
                    style={{
                      transform: `scale(${zoom * rotatedFitScale}) rotate(${rotation}deg)`,
                      transition: "transform 150ms",
                    }}
                  />
                )}
              </div>
              {/* Flex centering instead of start-1/2 + -translate-x-1/2: the logical/physical mix
                  shifts the toolbar a full width off-center under dir=rtl (Arabic). */}
              <div className="pointer-events-none absolute bottom-4 start-0 end-0 flex justify-center">
                <div className="pointer-events-auto flex items-center gap-3 rounded-lg bg-white px-3 py-2 shadow-md">
                  <IconComponent
                    name="zoomOut"
                    width="1.25rem"
                    height="1.25rem"
                    toolTipContent={t("Zoom Out")}
                    className="cursor-pointer"
                    onClick={zoomOut}
                  />
                  <span className="text-sm">{`${zoomPercent}%`}</span>
                  <IconComponent
                    name="zoomIn"
                    width="1.25rem"
                    height="1.25rem"
                    toolTipContent={t("Zoom In")}
                    className="cursor-pointer"
                    onClick={zoomIn}
                  />
                  <IconComponent
                    name="rotateLeft"
                    width="1.25rem"
                    height="1.25rem"
                    toolTipContent={t("Rotate Left")}
                    className="cursor-pointer"
                    onClick={rotateLeft}
                  />
                  <IconComponent
                    name="rotateRight"
                    width="1.25rem"
                    height="1.25rem"
                    toolTipContent={t("Rotate Right")}
                    className="cursor-pointer"
                    onClick={rotateRight}
                  />
                </div>
              </div>
            </div>
          </div>
        }
        footerContent={
          <ButtonComponent onClick={confirmImage} buttonType={BUTTON_TYPES.GRADIENT_BUTTON}>
            {t("Use Image")}
          </ButtonComponent>
        }
      />
    </>
  );
};

export default CameraCaptureModals;
